/**
 * Constants mapping skilled needs and scores to their descriptions
 * Based on the PAT (Pediatric Assessment Tool) questionnaire
 * This replaces the need to fetch descriptions from previous PAT assessments
 */

export interface SkilledNeedDescription {
  name: string
  score: string
  description: string
}

export const SKILLED_NEEDS_DESCRIPTIONS: SkilledNeedDescription[] = [
  // Bathing
  {
    name: 'Bathing',
    score: '0',
    description:
      'Member is able to bathe self in tub/shower independently including getting in and out of tub/shower, member can be verbally cued to bathe in tub/shower with set-up, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Bathing',
    score: '2',
    description: 'Member requires minimal to moderate assistance to complete bathing'
  },
  {
    name: 'Bathing',
    score: '4',
    description: 'Member requires maximum to total assistance to complete bathing'
  },

  // Braces, Splints, and/or Pressure Stockings
  {
    name: 'Braces, Splints, and/or Pressure Stockings',
    score: '0',
    description:
      'Member either does not have physician ordered application of braces, splints, or anti-embolic stockings, does not require assistance with application, or current ability is age appropriate'
  },
  {
    name: 'Braces, Splints, and/or Pressure Stockings',
    score: '1',
    description:
      'Member requires physician ordered application and removal of braces, splints, or antiembolic stockings daily'
  },
  {
    name: 'Braces, Splints, and/or Pressure Stockings',
    score: '2',
    description:
      'Member requires physician ordered application and removal of braces, splints, or anti-embolic stockings greater than once per day'
  },

  // Dressing
  {
    name: 'Dressing',
    score: '0',
    description:
      'Member is able to dress without assistance, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Dressing',
    score: '1',
    description: 'Member requires minimal to moderate assistance to dress'
  },
  {
    name: 'Dressing',
    score: '2',
    description: 'Member requires maximum to total assistance to dress'
  },

  // Eating or Oral Feedings
  {
    name: 'Eating or Oral Feedings',
    score: '0',
    description:
      'Member is able to independently chew and swallow without difficulties, the member does not eat by mouth, current ability is age appropriate, or the task is a personal care task'
  },
  {
    name: 'Eating or Oral Feedings',
    score: '2',
    description: 'Member requires minimal to moderate assistance during meals'
  },
  {
    name: 'Eating or Oral Feedings',
    score: '4',
    description: 'Member requires maximum to total assistance during meals'
  },

  // Enteral G-tube/J-tube feedings
  {
    name: 'Enteral G-tube/J-tube feedings',
    score: '0',
    description: 'Member does not require enteral feeds or task is not delegated to CNA'
  },
  {
    name: 'Enteral G-tube/J-tube feedings',
    score: '2',
    description: 'Tasks can be completed in less than 1 hour per day'
  },
  {
    name: 'Enteral G-tube/J-tube feedings',
    score: '4',
    description: 'Tasks can be completed in greater than 1 hour per day'
  },

  // Medication
  {
    name: 'Medication',
    score: '0',
    description: 'CNA does not have MED Authority'
  },
  {
    name: 'Medication',
    score: '2',
    description:
      'Member requires a CNA with a DORA approved MED Authority to administer medications and will provide medications'
  },

  // Mobility/Ambulation
  {
    name: 'Mobility/Ambulation',
    score: '0',
    description:
      'Member is able to independently walk/operate wheelchair or assistance devices on even and uneven surfaces, negotiate stairs with or without railings, has the ability to balance and bear weight, current ability is age appropriate, or the task is a personal care task'
  },
  {
    name: 'Mobility/Ambulation',
    score: '3',
    description:
      'Member is able to ambulate/operate wheelchair or assistance devices with minimal to moderate assistance'
  },
  {
    name: 'Mobility/Ambulation',
    score: '6',
    description:
      'Member is able to ambulate/operate wheelchair or assistance devices with maximum to total assistance'
  },

  // Modified meal preparation
  {
    name: 'Modified meal preparation',
    score: '0',
    description:
      'Member does not require meals with a modified consistency or task does not require oversight'
  },
  {
    name: 'Modified meal preparation',
    score: '2',
    description:
      'Member requires meals with a modified consistency and/or the CNA has oversight to perform task'
  },

  // Grooming
  {
    name: 'Grooming',
    score: '0',
    description:
      'Member is able to perform grooming tasks independently, current ability is age appropriate, or tasks are not personal care tasks'
  },
  {
    name: 'Grooming',
    score: '1',
    description: 'Member requires assistance to complete 1 grooming task'
  },
  {
    name: 'Grooming',
    score: '2',
    description: 'Member requires assistance to complete 2 grooming tasks'
  },
  {
    name: 'Grooming',
    score: '3',
    description: 'Member requires assistance to complete 3 grooming tasks'
  },
  {
    name: 'Grooming',
    score: '4',
    description:
      'Member is completely dependent and requires total assistance for all grooming tasks'
  },

  // Skin care (includes application of a physician ordered product)
  {
    name: 'Skincare',
    score: '0',
    description:
      'Member does not have a G-tube, J-tube, stoma, or broken skin requiring a physician ordered product or task is not performed by a CNA'
  },
  {
    name: 'Skincare',
    score: '1',
    description:
      'Member has a G-tube, J-tube, stoma, or broken skin requiring a physician ordered product at least daily'
  },

  // Skin Care
  {
    name: 'Skincare (OTC)',
    score: '0',
    description:
      'Member does not have a G-tube, J-tube, stoma, splints and/or braces, or broken skin'
  },
  {
    name: 'Skincare (OTC)',
    score: '1',
    description:
      'Member has a G-tube, J-tube, broken skin, splints and/or braces, or stomas requiring care or simple dressing changes on a daily basis'
  },

  // ROM/Exercise
  {
    name: 'ROM/Exercise',
    score: '0',
    description:
      'Member is able to exercise or is sufficiently active without assistance, requires a reminder to perform an ordered exercise program, task is performed by a licensed therapist, or task is a personal care task'
  },
  {
    name: 'ROM/Exercise',
    score: '2',
    description: 'Combined daily program consists of less than 1 hour per day'
  },
  {
    name: 'ROM/Exercise',
    score: '4',
    description: 'Combined daily program consists of greater than 1 or more hours per day'
  },

  // Chest Percussive Therapy
  {
    name: 'Chest Percussive Therapy',
    score: '0',
    description:
      'Member does not receive CPT, vest treatments, or cough assistance or task is not performed by the CNA'
  },
  {
    name: 'Chest Percussive Therapy',
    score: '2',
    description:
      'CPT, vest treatments, or cough assist is ordered and performed 1 to 2 times per day'
  },
  {
    name: 'Chest Percussive Therapy',
    score: '4',
    description:
      'CPT, vest treatments, or cough assist is ordered and performed 3 or more times per day'
  },

  // Oxygen Therapy
  {
    name: 'Oxygen Therapy',
    score: '0',
    description: 'No'
  },
  {
    name: 'Oxygen Therapy',
    score: '1',
    description: 'Yes'
  },

  // Member requires oral suctioning
  {
    name: 'Member requires oral suctioning',
    score: '0',
    description:
      'Member does not require oral suctioning or the member is able to remove secretions independently'
  },
  {
    name: 'Member requires oral suctioning',
    score: '1',
    description:
      'Member requires infrequent or as need (PRN) oral suctioning to remove oral secretions'
  },
  {
    name: 'Member requires oral suctioning',
    score: '2',
    description: 'Member requires frequent oral suctioning to remove oral secretions'
  },

  // Bowel Frequency
  {
    name: 'Bowel Frequency',
    score: '0',
    description: 'On average, member has a bowel movement twice per a day or less'
  },
  {
    name: 'Bowel Frequency',
    score: '1',
    description: 'On average, member has a bowel movement 3 to 4 times per day'
  },
  {
    name: 'Bowel Frequency',
    score: '2',
    description: 'On average, member has a bowel movement 5 or more times per day'
  },

  // Bowel Hygiene
  {
    name: 'Bowel Hygiene',
    score: '0',
    description:
      'Member is able to manage bowel hygiene safely, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Bowel Hygiene',
    score: '2',
    description: 'Member requires minimal to moderate assistance for bowel hygiene'
  },
  {
    name: 'Bowel Hygiene',
    score: '4',
    description:
      'Member requires maximum to total assistance for bowel hygiene; may include changing diapers and perineal care associated with diaper changes'
  },

  // Bowel Elimination - Ostomy
  {
    name: 'Bowel Elimination - Ostomy',
    score: '0',
    description: 'Member does not have an ostomy for bowel elimination'
  },
  {
    name: 'Bowel Elimination - Ostomy',
    score: '1',
    description: 'Member has an ostomy for bowel elimination'
  },

  // Bowel Program
  {
    name: 'Bowel Program',
    score: '0',
    description:
      'No bowel program, member receives an oral stool softener or laxative, or physician ordered bowel program consists of the use of digital stimulation, or over the counter suppositories ordered on an as needed basis'
  },
  {
    name: 'Bowel Program',
    score: '1',
    description:
      'Physician ordered bowel program consists of the use of digital stimulation or over the counter suppositories ordered at least on a weekly basis'
  },
  {
    name: 'Bowel Program',
    score: '4',
    description:
      'Physician ordered bowel program consists of the use of digital stimulation or over the counter suppositories ordered for every day'
  },

  // Catheter Care
  {
    name: 'Catheter Care',
    score: '0',
    description: 'No catheter care required'
  },
  {
    name: 'Catheter Care',
    score: '1',
    description:
      'Catheter may be emptied without a need to record output and indwelling catheter tubing needs to be disconnected/reconnected'
  },
  {
    name: 'Catheter Care',
    score: '2',
    description:
      "Catheter may be emptied and/or indwelling catheter tubing needs to be disconnected/reconnected, and catheter output must be recorded and reported to RN per physicians' orders"
  },

  // Menses
  {
    name: 'Menses',
    score: '0a',
    description: 'Member is male'
  },
  {
    name: 'Menses',
    score: '0b',
    description:
      "Member's menstrual cycle has not started or is controlled/halted due to medical or pharmaceutical intervention"
  },
  {
    name: 'Menses',
    score: '0c',
    description: 'Member is independent or requires minimal to moderate assistance with needs'
  },
  {
    name: 'Menses',
    score: '1',
    description: 'Member requires maximum to total assistance with needs'
  },

  // Urinary Hygiene
  {
    name: 'Urinary Hygiene',
    score: '0',
    description:
      'Member is able to manage urinary hygiene safely, has a catheter, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Urinary Hygiene',
    score: '1',
    description: 'Member requires minimal to moderate assistance to complete urinary hygiene'
  },
  {
    name: 'Urinary Hygiene',
    score: '3',
    description: 'Member requires maximum to total assistance to complete urinary hygiene'
  },

  // Positioning
  {
    name: 'Positioning',
    score: '0',
    description:
      'Member is able to shift weight and reposition without assistance, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Positioning',
    score: '2',
    description: 'Member requires minimal to moderate assistance for repositioning'
  },
  {
    name: 'Positioning',
    score: '4',
    description: 'Member requires maximum to total assistance for repositioning'
  },

  // Transfers
  {
    name: 'Transfers',
    score: '0',
    description:
      'Member is able to transfer independently, current ability is age appropriate, or task is a personal care task'
  },
  {
    name: 'Transfers',
    score: '3',
    description: 'Member is able to transfer with minimal to moderate assistance'
  },
  {
    name: 'Transfers',
    score: '6',
    description:
      'Member is able to transfer with maximum to total assistance, this may include 1-2 adults and/or a mechanical lift'
  },

  // Vital signs, physician ordered
  {
    name: 'Vital signs, physician ordered',
    score: '0',
    description: 'Vital Signs not included in care plan'
  },
  {
    name: 'Vital signs, physician ordered',
    score: '1',
    description:
      'Care plan includes temperature, pulse, and respiratory rate with parameters for nurse and/or physician notification'
  },

  // Vital signs, nurse ordered
  {
    name: 'Vital signs, nurse ordered',
    score: '0',
    description: 'Care plan does not include blood glucose and/or pulse oximetry'
  },
  {
    name: 'Vital signs, nurse ordered',
    score: '1',
    description:
      "Care plan includes blood glucose and/or pulse oximetry performed by a CNA and reported to the member's nurse or physician as ordered on the plan of care"
  }
]

/**
 * Helper function to get description for a skilled need and score
 */
export const getSkilledNeedDescription = (name: string, score: string): string => {
  const description = SKILLED_NEEDS_DESCRIPTIONS.find(
    (item) => item.name === name && item.score === score
  )
  return description?.description || ''
}
